import { J2Add, J2Subtract } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Card, CardMedia, IconButton, Typography } from '@mui/material'
import React from 'react'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { Product } from 'src/Libs/shop/models/system-configuration-components.model'
import { getImageUrlBySize } from 'src/Libs/shop/utils'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { QuantityAvailabilityIndicator } from '../../ShopProduct'
import { useProductCardStyles } from '../styles/product-card.styles'

interface SelectedConfiguredProduct {
  id: string
  qty: number
}

interface AdditionalComponentProductCardProps {
  productData: Product
  categoryId: string
  onMoreInfo: (productId: string) => void
  isProductSelected: boolean
  selectedProduct?: SelectedConfiguredProduct
  onDecreaseProductQuantity: (categoryId: string, selectedProductId: string, selectedProductQty: number) => void
  onIncreaseProductQuantity: (categoryId: string, selectedProductId: string) => void
  onAddProductToCart: (categoryId: string, productId: string, minPurchase: number, productHint: string | false) => void
  onRemoveProductFromCart: (categoryId: string, productId: string) => void
}

/*
 * Displays an additional component product card with selection and quantity controls
 */
const AdditionalComponentProductCard: React.FC<AdditionalComponentProductCardProps> = ({
  productData,
  categoryId,
  onMoreInfo,
  isProductSelected,
  selectedProduct,
  onDecreaseProductQuantity,
  onIncreaseProductQuantity,
  onAddProductToCart,
  onRemoveProductFromCart,
}) => {
  const { t } = useCustomTranslation()
  const { classes, cx } = useProductCardStyles()

  const unitPrice = currencyFormatter(productData.calculatedPrice.unitPrice)
  const price = currencyFormatter(productData?.calculatedPrice?.listPrice?.price) ?? currencyFormatter(productData?.calculatedPrice.unitPrice)

  const productAvailability = productData.extensions?.e3dcAvailability
  const productHint =
    productData.customFields.e3dcPopupActive && productData.customFields.e3dcPopupInfoText ? productData.customFields.e3dcPopupInfoText : false

  const productImageUrl = getImageUrlBySize(productData?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
  const isPriceApplied = Boolean(productData.extensions?.e3dcPriceApplied.state)

  const handleAddOrRemove = (): void => {
    if (isProductSelected) {
      if (selectedProduct) {
        onRemoveProductFromCart(categoryId, selectedProduct.id)
      }
      return
    }

    onAddProductToCart(categoryId, productData.id, productData.minPurchase, productHint)
  }

  return (
    <Card className={cx(classes.product, isProductSelected && classes.productActive)} key={productData.id}>
      {isProductSelected && <Typography className={classes.activeLabel}>{t('shop.common.selected')}</Typography>}
      <CardMedia
        className={classes.productImage}
        component="img"
        image={productImageUrl}
        alt="Product"
        onClick={() => {
          onMoreInfo(productData.id)
        }}
      />
      <Box className={classes.containerTextWrapper}>
        <Typography align="left" variant="h3">
          {productData.translated.name}
          <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
        </Typography>
      </Box>
      <Box>
        <Box className={classes.priceContainer}>
          <Box className={classes.flexPrice}>
            <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
              {unitPrice ? unitPrice : price}
            </Typography>
            {unitPrice < price && (
              <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                {price}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={classes.buttonContainer}>
          <Button
            className={classes.button}
            size="large"
            color="inherit"
            variant="outlined"
            fullWidth
            onClick={() => {
              onMoreInfo(productData.id)
            }}
          >
            {t('shop.common.more-info')}
          </Button>
          {isProductSelected ? (
            <Box className={classes.numCounter}>
              <IconButton
                onClick={() => {
                  selectedProduct && onDecreaseProductQuantity(categoryId, selectedProduct.id, selectedProduct.qty)
                }}
              >
                <J2Subtract className={classes.iconButtonBackwards} />
              </IconButton>
              <Typography>{selectedProduct?.qty}</Typography>
              <IconButton
                onClick={() => {
                  selectedProduct && onIncreaseProductQuantity(categoryId, selectedProduct.id)
                }}
              >
                <J2Add className={classes.iconButton} />
              </IconButton>
            </Box>
          ) : (
            <Button className={classes.button} disabled={!isPriceApplied} color="primary" variant="contained" fullWidth onClick={handleAddOrRemove}>
              {isProductSelected ? t('shop.common.remove') : t('shop.common.select')}
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  )
}

export default AdditionalComponentProductCard
